import React, { useMemo, useState } from "react";

export default function App() {
  const [page, setPage] = useState("home");

  const nav = useMemo(
    () => [
      { id: "home", label: "Home" },
      { id: "features", label: "Features" },
      { id: "how", label: "How It Works" },
      { id: "start", label: "Get Started" },
      { id: "pricing", label: "Pricing" },
    ],
    []
  );

  return (
    <div style={styles.page}>
      <Header page={page} setPage={setPage} nav={nav} />
      <main style={styles.main}>
        <div style={styles.container}>
          {page === "home" && <Home onCTA={() => setPage("start")} />}
          {page === "features" && <Features />}
          {page === "how" && <HowItWorks onCTA={() => setPage("start")} />}
          {page === "start" && <GetStarted onPricing={() => setPage("pricing")} />}
          {page === "pricing" && <Pricing />}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Header({ page, setPage, nav }) {
  return (
    <header style={styles.headerWrap}>
      <div style={styles.container}>
        <div style={styles.headerTop}>
          <div>
            <div style={styles.brandRow}>
              <div style={styles.badge}>OFFLINE</div>
              <div style={styles.brand}>Offline Survival Master</div>
            </div>
            <div style={styles.subtitle}>
              Survival • Preparedness • Education — any region, terrain, or season
            </div>
          </div>

          <button style={styles.primaryBtn} onClick={() => setPage("start")}>
            Download / Get Started
          </button>
        </div>

        <nav style={styles.nav}>
          {nav.map((n) => (
            <button
              key={n.id}
              onClick={() => setPage(n.id)}
              style={{
                ...styles.navBtn,
                ...(page === n.id ? styles.navBtnActive : null),
              }}
            >
              {n.label}
            </button>
          ))}
        </nav>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer style={styles.footer}>
      <div style={styles.container}>
        <div style={styles.footerGrid}>
          <div>
            <div style={styles.footerBrand}>Offline Survival Master</div>
            <div style={styles.footerText}>
              Built for real-world preparedness and everyday adventure — with offline-first design.
            </div>
          </div>
          <div>
            <div style={styles.footerTitle}>Core Pillars</div>
            <div style={styles.footerText}>Offline • Global • Seasonal • Step-by-step</div>
          </div>
          <div>
            <div style={styles.footerTitle}>Roadmap</div>
            <div style={styles.footerText}>
              Expanded languages • Accessibility options • Deeper databases
            </div>
          </div>
        </div>

        <div style={styles.footerBottom}>
          © {new Date().getFullYear()} Offline Survival Master
        </div>
      </div>
    </footer>
  );
}

function Hero({ title, lead, chips }) {
  return (
    <div style={styles.hero}>
      <div style={styles.heroTop}>
        <div style={styles.heroTitle}>{title}</div>
        <div style={styles.heroLead}>{lead}</div>
        <div style={styles.chips}>
          {chips.map((c) => (
            <span key={c} style={styles.chip}>
              {c}
            </span>
          ))}
        </div>
      </div>

      <div style={styles.heroCards}>
        <InfoCard
          title="Offline-First"
          text="Critical guidance available when you have zero signal — built for real conditions."
        />
        <InfoCard
          title="Global Coverage"
          text="Designed for forest, desert, mountain, jungle, ocean/coastal, and urban survival."
        />
        <InfoCard
          title="Season-Aware"
          text="Winter cold risk, summer heat, storms, wildfire, flooding — guidance that fits the season."
        />
      </div>
    </div>
  );
}

function InfoCard({ title, text }) {
  return (
    <div style={styles.card}>
      <div style={styles.cardTitle}>{title}</div>
      <div style={styles.cardText}>{text}</div>
    </div>
  );
}

function Section({ title, children, right }) {
  return (
    <div style={styles.section}>
      <div style={styles.sectionHead}>
        <div>
          <div style={styles.sectionTitle}>{title}</div>
        </div>
        {right ? <div>{right}</div> : null}
      </div>
      <div style={styles.sectionBody}>{children}</div>
    </div>
  );
}

function Home({ onCTA }) {
  return (
    <>
      <Hero
        title="Survive anywhere. Learn anytime."
        lead="A global survival, preparedness, and education system designed to work offline — useful for camping and skill-building, and dependable in real emergencies."
        chips={[
          "Works offline",
          "Any terrain",
          "Any season",
          "Step-by-step guidance",
          "Global languages (expanding)",
        ]}
      />

      <div style={styles.grid2}>
        <Section
          title="Who it’s for"
          right={
            <span style={styles.kicker}>
              Not only emergencies — also learning & adventure
            </span>
          }
        >
          <ul style={styles.list}>
            <li>Survivalists & preppers</li>
            <li>Campers, hikers, hunters, and outdoor workers</li>
            <li>Off-grid living & self-reliance</li>
            <li>Travelers in unfamiliar regions</li>
            <li>Anyone building real-world survival skills</li>
          </ul>
        </Section>

        <Section title="Why subscription-only (no lifetime)">
          <div style={styles.p}>
            Survival guidance must stay current: seasonal hazards, safety best-practices, region updates,
            and database expansion.
          </div>
          <div style={styles.p}>
            Subscription supports continuous improvements — so the system grows with the real world.
          </div>
        </Section>
      </div>

      <Section
        title="Ready to start?"
        right={
          <button style={styles.primaryBtn} onClick={onCTA}>
            Download / Get Started
          </button>
        }
      >
        <div style={styles.p}>
          Start with the essentials: shelter, water, fire, navigation, first aid, signaling, and food —
          organized in the correct survival order.
        </div>
      </Section>
    </>
  );
}

function Features() {
  return (
    <>
      <Hero
        title="Features"
        lead="Designed for everyday use and real emergencies — with offline-first reliability and global survival coverage."
        chips={["Offline", "Global", "Season-aware", "Step-by-step", "Expanding databases"]}
      />

      <div style={styles.grid3}>
        <InfoCard
          title="Offline Survival System"
          text="Once downloaded, key content works without Wi-Fi, cell service, or reliable power."
        />
        <InfoCard
          title="Terrain Coverage"
          text="Forest, desert, mountain, jungle, ocean/coastal, and urban survival guidance."
        />
        <InfoCard
          title="Season-Aware Guidance"
          text="Cold exposure, heat risk, storms, wildfire, flooding, and shifting conditions."
        />
        <InfoCard
          title="Step-by-Step Modules"
          text="Shelter, fire, water, navigation, first aid, signaling, and food — in the right order."
        />
        <InfoCard
          title="Wild Food & Plants"
          text="Expanding region-based information with safety-first emphasis and prep steps."
        />
        <InfoCard
          title="Global Languages (Expanding)"
          text="Built for worldwide access — language coverage grows over time."
        />
      </div>
    </>
  );
}

function HowItWorks({ onCTA }) {
  return (
    <>
      <Hero
        title="How It Works"
        lead="Simple when everything else is chaos. Learn calmly — rely on it when conditions turn critical."
        chips={["Download once", "Works offline", "Choose focus", "Follow steps", "Stay updated"]}
      />

      <div style={styles.grid2}>
        <Section title="1) Download once">
          <div style={styles.p}>
            Install the app and download offline content. Core survival guidance becomes available
            even without signal.
          </div>
        </Section>
        <Section title="2) Choose language + environment">
          <div style={styles.p}>
            Select your language and focus. The system is designed to apply across regions, terrain types,
            and seasons.
          </div>
        </Section>
        <Section title="3) Follow step-by-step guidance">
          <div style={styles.p}>
            Clear instructions for shelter, water, fire, navigation, first aid, signaling, and food —
            presented to work under stress.
          </div>
        </Section>
        <Section title="4) Keep it current with updates">
          <div style={styles.p}>
            Subscription supports continuous updates: safety refinements, new regions, expanded databases,
            and language growth.
          </div>
        </Section>
      </div>

      <Section
        title="Get it set up in minutes"
        right={
          <button style={styles.primaryBtn} onClick={onCTA}>
            Download / Get Started
          </button>
        }
      >
        <div style={styles.p}>
          Prepare once and carry it everywhere — for camping, education, travel, off-grid life, and real emergencies.
        </div>
      </Section>
    </>
  );
}

function GetStarted({ onPricing }) {
  return (
    <>
      <Hero
        title="Get Started"
        lead="Prepare once and carry it everywhere. Learn calmly, rely on it when it matters."
        chips={["Fast setup", "Offline ready", "All seasons", "All terrains", "Guided modules"]}
      />

      <div style={styles.grid2}>
        <Section title="Start in 4 steps">
          <ol style={styles.olist}>
            <li>Download the app</li>
            <li>Select your language</li>
            <li>Download offline content</li>
            <li>Learn, practice, and stay prepared across all seasons</li>
          </ol>
        </Section>

        <Section title="What you’ll get">
          <ul style={styles.list}>
            <li>Essential survival priorities in the correct order</li>
            <li>Offline-first reliability for real conditions</li>
            <li>Global coverage across regions and terrains</li>
            <li>Season-aware safety guidance</li>
            <li>Continuous updates and expansion</li>
          </ul>
        </Section>
      </div>

      <Section
        title="Pricing is next"
        right={
          <button style={styles.secondaryBtn} onClick={onPricing}>
            View Pricing
          </button>
        }
      >
        <div style={styles.p}>
          Subscription keeps the system current and expanding — with new content, safety improvements, and language growth.
        </div>
      </Section>
    </>
  );
}

function Pricing() {
  return (
    <>
      <Hero
        title="Pricing"
        lead="Subscription-only by design. Survival guidance must stay current — and the system expands over time."
        chips={["Monthly", "Annual (Best value)", "Updates included", "No lifetime", "Cancel anytime"]}
      />

      <div style={styles.grid2}>
        <Section title="Monthly Access">
          <div style={styles.priceRow}>
            <div>
              <div style={styles.priceName}>Monthly</div>
              <div style={styles.priceHint}>Best for short trips, seasonal use, or trying it out.</div>
            </div>
            <div style={styles.priceTag}>$9.99</div>
          </div>
          <div style={styles.pDim}>Cancel anytime.</div>
        </Section>

        <Section title="Annual Access (Best Value)">
          <div style={styles.priceRow}>
            <div>
              <div style={styles.priceName}>Annual</div>
              <div style={styles.priceHint}>Year-round preparedness — all terrains, all seasons.</div>
            </div>
            <div style={styles.priceTag}>$79</div>
          </div>
          <div style={styles.pDim}>Most value per month.</div>
        </Section>
      </div>

      <Section title="Why no lifetime access">
        <div style={styles.p}>
          Safety guidance changes. Regions and risks shift. Databases grow. Subscription ensures the system stays current
          and responsible over time.
        </div>
      </Section>

      <Section title="Next: connect payments">
        <div style={styles.p}>
          Once you’re ready, we’ll connect Stripe so users can subscribe directly from the Pricing page.
        </div>
      </Section>
    </>
  );
}

const styles = {
  page: {
    background: "radial-gradient(1200px 700px at 20% 0%, #0f1c2b 0%, #070a0f 60%, #05070b 100%)",
    minHeight: "100vh",
    color: "#e5e7eb",
  },
  container: { maxWidth: 1040, margin: "0 auto", padding: "0 18px" },
  headerWrap: {
    position: "sticky",
    top: 0,
    zIndex: 50,
    backdropFilter: "blur(10px)",
    background: "rgba(6, 9, 14, 0.65)",
    borderBottom: "1px solid rgba(148,163,184,0.15)",
  },
  headerTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    padding: "16px 0 10px",
  },
  brandRow: { display: "flex", alignItems: "center", gap: 10 },
  badge: {
    fontSize: 12,
    letterSpacing: 1.4,
    fontWeight: 900,
    padding: "6px 10px",
    borderRadius: 999,
    background: "rgba(34,197,94,0.12)",
    border: "1px solid rgba(34,197,94,0.35)",
    color: "#86efac",
  },
  brand: { fontSize: 22, fontWeight: 900, letterSpacing: 0.2 },
  subtitle: { fontSize: 13.5, color: "rgba(226,232,240,0.75)", marginTop: 6 },
  nav: { display: "flex", flexWrap: "wrap", gap: 10, paddingBottom: 14 },
  navBtn: {
    background: "rgba(148,163,184,0.08)",
    border: "1px solid rgba(148,163,184,0.18)",
    color: "#e5e7eb",
    padding: "9px 12px",
    borderRadius: 12,
    cursor: "pointer",
    fontWeight: 700,
    fontSize: 13,
  },
  navBtnActive: {
    background: "rgba(34,197,94,0.14)",
    border: "1px solid rgba(34,197,94,0.35)",
    color: "#d1fae5",
  },
  primaryBtn: {
    background: "linear-gradient(180deg, rgba(34,197,94,1) 0%, rgba(22,163,74,1) 100%)",
    border: "none",
    color: "#051009",
    padding: "11px 14px",
    borderRadius: 14,
    fontWeight: 900,
    cursor: "pointer",
    boxShadow: "0 10px 24px rgba(34,197,94,0.18)",
    whiteSpace: "nowrap",
  },
  secondaryBtn: {
    background: "rgba(96,165,250,0.18)",
    border: "1px solid rgba(96,165,250,0.35)",
    color: "#dbeafe",
    padding: "11px 14px",
    borderRadius: 14,
    fontWeight: 900,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  main: { padding: "18px 0 26px" },

  hero: {
    border: "1px solid rgba(148,163,184,0.18)",
    background: "linear-gradient(180deg, rgba(15,23,42,0.55) 0%, rgba(2,6,23,0.35) 100%)",
    borderRadius: 22,
    padding: 18,
    boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
    marginTop: 12,
    marginBottom: 16,
  },
  heroTop: { padding: "6px 6px 12px" },
  heroTitle: { fontSize: 34, fontWeight: 950, letterSpacing: -0.4, marginBottom: 10 },
  heroLead: { fontSize: 15.5, color: "rgba(226,232,240,0.78)", lineHeight: 1.6 },
  chips: { display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 },
  chip: {
    fontSize: 12.5,
    padding: "7px 10px",
    borderRadius: 999,
    background: "rgba(148,163,184,0.08)",
    border: "1px solid rgba(148,163,184,0.16)",
    color: "rgba(226,232,240,0.88)",
  },
  heroCards: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginTop: 10 },

  card: {
    border: "1px solid rgba(148,163,184,0.16)",
    background: "rgba(2,6,23,0.35)",
    borderRadius: 18,
    padding: 14,
  },
  cardTitle: { fontSize: 14.5, fontWeight: 900, marginBottom: 6 },
  cardText: { fontSize: 13.5, color: "rgba(226,232,240,0.78)", lineHeight: 1.55 },

  section: {
    border: "1px solid rgba(148,163,184,0.16)",
    background: "rgba(2,6,23,0.30)",
    borderRadius: 18,
    padding: 16,
    marginBottom: 14,
  },
  sectionHead: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 },
  sectionTitle: { fontSize: 18, fontWeight: 950, marginBottom: 10 },
  sectionBody: { color: "rgba(226,232,240,0.82)", lineHeight: 1.65, fontSize: 14.5 },
  p: { marginBottom: 10 },
  pDim: { color: "rgba(226,232,240,0.65)", fontSize: 13.5 },
  kicker: {
    fontSize: 12.5,
    padding: "7px 10px",
    borderRadius: 999,
    background: "rgba(96,165,250,0.14)",
    border: "1px solid rgba(96,165,250,0.28)",
    color: "#dbeafe",
    fontWeight: 800,
  },
  list: { margin: 0, paddingLeft: 18 },
  olist: { margin: 0, paddingLeft: 18 },

  grid2: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 14 },
  grid3: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 },

  priceRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 },
  priceName: { fontSize: 16, fontWeight: 950 },
  priceHint: { color: "rgba(226,232,240,0.70)", fontSize: 13.5, marginTop: 4, lineHeight: 1.4 },
  priceTag: {
    fontSize: 22,
    fontWeight: 950,
    padding: "10px 12px",
    borderRadius: 14,
    background: "rgba(34,197,94,0.12)",
    border: "1px solid rgba(34,197,94,0.30)",
    color: "#bbf7d0",
    minWidth: 90,
    textAlign: "center",
  },

  footer: {
    marginTop: 26,
    borderTop: "1px solid rgba(148,163,184,0.16)",
    background: "rgba(2,6,23,0.35)",
    padding: "22px 0",
  },
  footerGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 14,
    marginBottom: 14,
  },
  footerBrand: { fontWeight: 950, fontSize: 14.5, marginBottom: 6 },
  footerTitle: { fontWeight: 950, fontSize: 13.5, marginBottom: 6, color: "rgba(226,232,240,0.9)" },
  footerText: { fontSize: 13.5, color: "rgba(226,232,240,0.72)", lineHeight: 1.5 },
  footerBottom: { fontSize: 12.5, color: "rgba(226,232,240,0.6)" },
};